import { Calendar, CheckCircle, ChevronDown, ChevronUp, Circle, Clock, Lock, Map, MessageCircle, Search } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ROUTES from '@/ROUTES'
import { usePaywallModal } from '@/stripe/PayWallModal'
import { usePaywall } from '@/stripe/PayWallProvider'

import { IRoadmapStep } from '@/MODELS/roadmapStep.model'

import { api } from '@/services/api.service'
import analytics from '@/services/analytics.service'
import { useAuth } from '@/services/authentication.service'

import Button from '@/components/Button'
import { DataView } from '@/components/data-view/DataView'
import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'
import { TaskCheckbox } from '@/components/TaskCheckBox'

function OverviewSection({
    steps,
    currentStepIndex,
}: {
    steps: IRoadmapStep[]
    currentStepIndex: number
}) {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <div className='mb-6 bg-white rounded-xl border border-gray-200 shadow-sm'>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className='flex justify-between items-center p-4 w-full text-left'
            >
                <div className='flex gap-3 items-center'>
                    <Map className='w-5 h-5 text-brand-500' />
                    <div>
                        <h3 className='text-base font-semibold text-gray-900'>Overview</h3>
                        <p className='text-sm text-gray-500'>Where you are on your journey</p>
                    </div>
                </div>
                {isOpen ? (
                    <ChevronUp className='w-5 h-5 text-gray-400' />
                ) : (
                    <ChevronDown className='w-5 h-5 text-gray-400' />
                )}
            </button>

            {isOpen && (
                <div className='px-4 pb-4'>
                    <div className='relative'>
                        {steps.map((step, index) => {
                            const isCurrent = index === currentStepIndex
                            const isCompleted = step.isComplete
                            const isLast = index === steps.length - 1

                            return (
                                <div key={step._id} className='relative flex gap-3'>
                                    {/* Vertical line + icon column */}
                                    <div className='flex flex-col items-center'>
                                        <div className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${
                                            isCurrent
                                                ? 'bg-brand-100'
                                                : isCompleted
                                                  ? 'bg-green-50'
                                                  : 'bg-gray-50'
                                        }`}>
                                            {isCompleted ? (
                                                <CheckCircle className='w-4 h-4 text-green-500' />
                                            ) : isCurrent ? (
                                                <Clock className='w-4 h-4 text-brand-500' />
                                            ) : (
                                                <Circle className='w-4 h-4 text-gray-300' />
                                            )}
                                        </div>
                                        {!isLast && (
                                            <div className='w-0.5 flex-1 min-h-[8px] bg-gray-200' />
                                        )}
                                    </div>
                                    {/* Content */}
                                    <div className={`flex items-center gap-2 flex-1 pb-3 text-sm ${
                                        isCurrent
                                            ? 'text-brand-700 font-semibold'
                                            : isCompleted
                                              ? 'text-gray-400'
                                              : 'text-gray-600'
                                    }`}>
                                        <span className='flex-1'>{step.title}</span>
                                        {isCurrent && (
                                            <span className='text-xs font-medium text-brand-500'>You are here</span>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <p className='mt-4 text-xs text-gray-400 italic'>
                        Families move through these at different paces. It's normal to revisit earlier steps as needs change.
                    </p>
                </div>
            )}
        </div>
    )
}

function PastMilestonesSection({
    completedSteps,
    expandedCards,
    onToggle,
    refresh,
    navigate,
}: {
    completedSteps: IRoadmapStep[]
    expandedCards: Record<string, boolean>
    onToggle: (id: string, e: React.MouseEvent) => void
    refresh: () => void
    navigate: (path: string) => void
}) {
    const [isOpen, setIsOpen] = useState(false)

    if (completedSteps.length === 0) return null

    return (
        <div className='mb-6 bg-white rounded-xl border border-gray-200 shadow-sm'>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className='flex justify-between items-center p-4 w-full text-left'
            >
                <div className='flex gap-3 items-center'>
                    <CheckCircle className='w-5 h-5 text-green-500' />
                    <div>
                        <h3 className='text-base font-semibold text-gray-900'>
                            Past Milestones ({completedSteps.length})
                        </h3>
                        <p className='text-sm text-gray-500'>Milestones you've completed</p>
                    </div>
                </div>
                {isOpen ? (
                    <ChevronUp className='w-5 h-5 text-gray-400' />
                ) : (
                    <ChevronDown className='w-5 h-5 text-gray-400' />
                )}
            </button>

            {isOpen && (
                <div className='relative px-4 pb-4'>
                    <div className='absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-gray-200'></div>
                    <div className='space-y-6'>
                        {completedSteps.map((step, index) => (
                            <StepCard
                                key={step._id}
                                step={step}
                                index={index}
                                status='completed'
                                refresh={refresh}
                                navigate={navigate}
                                isExpanded={expandedCards[step._id]}
                                onToggle={(e) => onToggle(step._id, e)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function JourneyMapPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { hasAccess, subscription } = usePaywall()
    const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({})
    const [roadmapInProgress, setRoadmapInProgress] = useState(false)

    // A sponsored family whose roadmap is being prepared by their provider sees a
    // "your provider is preparing your roadmap" state instead of an empty roadmap.
    // Framed as provider-led work — no mention of AI or generation.
    useEffect(() => {
        let cancelled = false
        async function loadRoadmapStatus() {
            const response = await api<{ roadmapInProgress?: boolean }>(ROUTES.app.sponsorship)
            if (!cancelled && response.status && response.status < 400 && response.data) {
                setRoadmapInProgress(!!response.data.roadmapInProgress)
            }
        }
        loadRoadmapStatus()
        return () => {
            cancelled = true
        }
    }, [])

    // Determine if user has a paid subscription (not just free access under threshold)
    const hasActiveSubscription = !!subscription && (
        ['active', 'trialing'].includes(subscription.subscriptionStatus) ||
        !!(subscription.benefitsEndDate && new Date(subscription.benefitsEndDate) > new Date())
    )

    const { showSubscriptionModal } = usePaywallModal()
    const toggleCard = (id: string, e: React.MouseEvent, allowExpand?: boolean) => {
        if (!hasAccess && !allowExpand) {
            showSubscriptionModal()
            return
        }
        e.stopPropagation()
        setExpandedCards((prev) => ({
            ...prev,
            [id]: !prev[id],
        }))
    }

    return (
        <DataView<IRoadmapStep> endpoint={ROUTES.app.roadmap}>
            {({ data, isLoading, get }) => {
                const filtered = data.filter((step) => !step.isExpired)
                const sortedData = filtered.sort((a, b) => (a.order || 0) - (b.order || 0))

                const currentStepIndex = sortedData.findIndex((step) => !step.isComplete)
                const currentStep = sortedData[currentStepIndex]

                const completedSteps = sortedData.filter((step) => step.isComplete)
                const activeSteps = sortedData.filter((step) => !step.isComplete)

                useEffect(() => {
                    if (!hasAccess) return
                    if (!currentStep?._id && !currentStep?.isComplete) return

                    setExpandedCards({ [currentStep._id]: true })
                }, [currentStep?._id])

                return (
                    <div className='flex-1 mx-auto w-full max-w-7xl'>
                        <SectionHeader title={'My Journey'} subtitle='Your personalized roadmap' />
                        <main className='px-3 py-8 sm:mx-10 sm:px-6'>
                            <h2 className='mb-6 text-xl font-semibold text-gray-900'>My Roadmap</h2>
                            {isLoading ? (
                                <div className='flex justify-center items-center py-12'>
                                    <Loading mode='block' isLoading={isLoading} text='Loading your roadmap...' />
                                </div>
                            ) : sortedData && sortedData.length > 0 ? (
                                <>
                                    <OverviewSection
                                        steps={sortedData}
                                        currentStepIndex={currentStepIndex}
                                    />

                                    <PastMilestonesSection
                                        completedSteps={completedSteps}
                                        expandedCards={expandedCards}
                                        onToggle={(id, e) => toggleCard(id, e, true)}
                                        refresh={get}
                                        navigate={navigate}
                                    />

                                    {activeSteps.length > 0 && (
                                        <div className='relative'>
                                            <div className='absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-gray-200'></div>

                                            <div className='space-y-6'>
                                                {activeSteps.map((step, index) => {
                                                    const globalIndex = sortedData.indexOf(step)
                                                    const isCurrent = globalIndex === currentStepIndex

                                                    let status: 'completed' | 'in-progress' | 'upcoming' = 'upcoming'
                                                    if (isCurrent || (currentStepIndex === -1 && index === 0)) {
                                                        status = 'in-progress'
                                                    }

                                                    // Free users can only see steps in the first active milestone
                                                    const isLocked = !hasActiveSubscription && index > 0

                                                    return (
                                                        <StepCard
                                                            key={step._id}
                                                            step={step}
                                                            index={globalIndex}
                                                            status={status}
                                                            refresh={get}
                                                            navigate={navigate}
                                                            isExpanded={expandedCards[step._id]}
                                                            onToggle={(e) => toggleCard(step._id, e, !isLocked)}
                                                            isCurrent={isCurrent}
                                                            isLocked={isLocked}
                                                            onSubscribe={showSubscriptionModal}
                                                        />
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : roadmapInProgress ? (
                                <div className='flex flex-col justify-center items-center py-16 bg-white rounded-xl border-2 border-dashed border-brand-200'>
                                    <div className='px-4 max-w-md text-center'>
                                        <Clock className='mx-auto mb-4 w-12 h-12 text-brand-400' />
                                        <p className='text-lg font-medium text-gray-700'>
                                            Your provider is preparing your personalized roadmap
                                        </p>
                                        <p className='mt-1 text-sm text-gray-500'>
                                            You&apos;ll see it here as soon as it&apos;s ready.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className='flex flex-col justify-center items-center py-16 bg-white rounded-xl border-2 border-gray-200 border-dashed'>
                                    <div className='text-center'>
                                        <Calendar className='mx-auto mb-4 w-12 h-12 text-gray-400' />
                                        <p className='text-lg font-medium text-gray-600'>No roadmap steps available</p>
                                        <p className='mt-1 text-sm text-gray-400'>
                                            Your personalized journey will appear here
                                        </p>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                )
            }}
        </DataView>
    )
}

function StepCard({
    step,
    index,
    status,
    refresh,
    navigate,
    isExpanded,
    onToggle,
    isCurrent,
    isLocked,
    onSubscribe,
}: {
    step: IRoadmapStep
    index: number
    status: 'completed' | 'in-progress' | 'upcoming'
    refresh: () => void
    navigate: (path: string) => void
    isExpanded: boolean
    onToggle: (e: React.MouseEvent) => void
    isCurrent?: boolean
    isLocked?: boolean
    onSubscribe?: () => void
}) {
    const completedTasks = step.tasks.filter((task) => task.isChecked).length
    const calculateProgress = () => {
        if (!step.tasks || step.tasks.length === 0) return 0

        return Math.round((completedTasks / step.tasks.length) * 100)
    }

    const progress = calculateProgress()
    const isCompleted = status === 'completed'

    return (
        <div className='relative'>
            <div className='flex absolute left-0 z-10 justify-center items-center w-8 h-8 bg-white rounded-full sm:left-4'>
                {isCompleted ? (
                    <CheckCircle className='w-8 h-8 text-green-500' />
                ) : status === 'in-progress' ? (
                    <div className='flex justify-center items-center w-8 h-8 rounded-full bg-brand-400'>
                        <Clock className='w-4 h-4 text-white' />
                    </div>
                ) : (
                    <Circle className='w-8 h-8 text-gray-300' />
                )}
            </div>

            <div className='ml-12 sm:ml-16'>
                <div
                    onClick={(e) => {
                        if (!isExpanded && !isLocked) {
                            onToggle(e)
                        }
                    }}
                    className={`bg-white rounded-xl shadow-sm border transition-all
                    ${isLocked ? 'opacity-75' : 'cursor-pointer'}
                    ${status === 'in-progress' && !isLocked ? 'shadow-brand-100' : 'border-gray-200'}
                    ${isCompleted ? 'opacity-90' : ''}
                `}
                >
                    <div className='p-6'>
                        <div className='flex justify-between items-center mb-3'>
                            <div className='flex gap-3 items-center'>
                                <span
                                    className={`text-xs font-semibold px-3 py-1 rounded-full
                                    ${status === 'completed' ? 'bg-green-100 text-green-700' : ''}
                                    ${status === 'in-progress' ? 'bg-brand-200/50 text-brand-900' : ''}
                                    ${status === 'upcoming' ? 'bg-gray-100 text-gray-600' : ''}
                                `}
                                >
                                    {status === 'completed'
                                        ? 'Completed'
                                        : status === 'in-progress'
                                          ? 'In Current Focus'
                                          : 'Upcoming'}
                                </span>
                            </div>
                            {isLocked ? (
                                <Lock className='w-4 h-4 text-gray-400' />
                            ) : !isExpanded ? (
                                <div className='flex gap-2 items-center'>
                                    <div className='flex items-center'>
                                        <span
                                            onClick={(e) => {
                                                onToggle(e)
                                            }}
                                            className='text-sm text-brand-700/70'
                                        >
                                            Show more
                                        </span>
                                    </div>

                                    <ChevronDown
                                        onClick={(e) => {
                                            onToggle(e)
                                        }}
                                        className='w-5 h-5 text-brand-700'
                                    />
                                </div>
                            ) : null}
                        </div>
                        <h3 className='mb-2 text-lg font-semibold text-gray-900'>{step.title}</h3>

                        {step.description && <p className='mb-4 text-sm text-gray-600'>{step.description}</p>}

                        <div className='flex gap-2 items-center mb-4 text-xs text-gray-500'>
                            <Calendar className='w-5 h-4' />
                            <span>{step.timeframe || '1-2 weeks'}</span>

                            {isExpanded && (
                                <div className='flex gap-4 items-center mx-2'>
                                    <div className='flex items-center'>
                                        <span
                                            onClick={(e) => {
                                                onToggle(e)
                                            }}
                                            className='text-sm text-brand-700/70'
                                        >
                                            Show less
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isLocked && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onSubscribe?.()
                                }}
                                className='flex gap-2 items-center justify-center w-full p-3 rounded-xl border border-brand-200 text-brand-600 bg-brand-50/50 hover:bg-brand-100 transition-colors cursor-pointer'
                            >
                                <Lock className='w-4 h-4' />
                                <span className='text-sm font-medium'>Subscribe to see recommended steps</span>
                            </button>
                        )}

                        {!isLocked && isExpanded && (
                            <>
                                <div className='py-2 mb-4 rounded-xl'>
                                    <div className='flex justify-between items-center mb-2'>
                                        <span className='text-sm font-medium text-gray-700'>Progress</span>

                                        <span className='text-sm text-gray-500'>
                                            {' '}
                                            {`${completedTasks} /${step.tasks.length}`}
                                        </span>
                                    </div>
                                    <div className='w-full h-2 rounded-full bg-secondary-200'>
                                        <div
                                            className='h-2 rounded-full transition-all duration-300 ease-out bg-brand-500'
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>

                                {step.tasks && step.tasks.length > 0 && (
                                    <div className='mb-4'>
                                        <h4 className='mb-3 text-sm font-medium text-gray-700'>Recommended Steps</h4>
                                        <div className='space-y-2'>
                                            {step.tasks.map((task) => (
                                                <TaskCheckbox
                                                    key={task._id}
                                                    task={task}
                                                    stepId={step._id}
                                                    refresh={refresh}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className='space-y-3'>
                                    <button
                                        onClick={() => {
                                            analytics.trackFeatureUsage('roadmap_chat', 'accessed', 1)
                                            navigate(`/chat?roadmap=${step._id}`)
                                        }}
                                        className='flex gap-3 items-center p-3 w-full text-left rounded-full border transition-colors text-brand-600 bg-brand-50/80 hover:bg-brand-100'
                                    >
                                        <MessageCircle className='w-4 h-4' />
                                        <span className='text-sm font-medium'>Ask AI for help with these steps</span>
                                    </button>

                                    <button
                                        onClick={() => navigate('/resource-landing')}
                                        className='flex gap-3 items-center p-3 w-full text-left rounded-full border transition-colors text-brand-600 bg-brand-50 hover:bg-brand-100'
                                    >
                                        <Search className='w-4 h-4' />
                                        <span className='text-sm font-medium'>Go to Resource Library</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
